import React, { useState } from 'react';
import { useAppStore } from '../store';
import {
    Folder,
    FolderOpen,
    FileCode,
    FileType2,
    FileJson,
    FileText,
    ChevronRight,
    ChevronDown,
    Plus,
    Trash2,
    Upload,
    FolderPlus,
    History,
    Files,
    Clock,
    Sparkles
} from 'lucide-react';

// File icon mapping
function getFileIcon(path: string) {
    const ext = path.split('.').pop()?.toLowerCase();
    const icons: Record<string, React.ReactNode> = {
        tsx: <FileCode className="w-4 h-4 text-cyan-400" />,
        ts: <FileCode className="w-4 h-4 text-blue-400" />,
        jsx: <FileCode className="w-4 h-4 text-yellow-400" />,
        js: <FileCode className="w-4 h-4 text-yellow-500" />,
        css: <FileType2 className="w-4 h-4 text-purple-400" />,
        json: <FileJson className="w-4 h-4 text-amber-400" />,
        html: <FileText className="w-4 h-4 text-orange-400" />,
        md: <FileText className="w-4 h-4 text-slate-400" />,
    };
    return icons[ext || ''] || <FileText className="w-4 h-4 text-text-muted" />;
}

// Build tree structure from flat file paths
interface TreeNode {
    name: string;
    path: string;
    type: 'file' | 'folder';
    children?: TreeNode[];
}

function buildFileTree(files: Map<string, { path: string }>): TreeNode[] {
    const root: TreeNode[] = [];

    for (const [path] of files) {
        const parts = path.split('/');
        let current = root;
        let currentPath = '';

        for (let i = 0; i < parts.length; i++) {
            const part = parts[i];
            currentPath = currentPath ? `${currentPath}/${part}` : part;
            const isFile = i === parts.length - 1;

            let node = current.find(n => n.name === part);

            if (!node) {
                node = {
                    name: part,
                    path: currentPath,
                    type: isFile ? 'file' : 'folder',
                    children: isFile ? undefined : [],
                };
                current.push(node);
            }

            if (!isFile && node.children) {
                current = node.children;
            }
        }
    }

    // Sort: folders first, then files
    const sortNodes = (nodes: TreeNode[]) => {
        nodes.sort((a, b) => {
            if (a.type !== b.type) return a.type === 'folder' ? -1 : 1;
            return a.name.localeCompare(b.name);
        });
        nodes.forEach(n => n.children && sortNodes(n.children));
    };
    sortNodes(root);

    return root;
}

// Tree node component
function TreeNodeItem({ node, depth = 0 }: { node: TreeNode; depth?: number }) {
    const { activeFilePath, setActiveFile, deleteFile } = useAppStore();
    const [isOpen, setIsOpen] = useState(depth < 2);
    const isActive = activeFilePath === node.path;

    if (node.type === 'folder') {
        return (
            <div>
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 text-sm text-left
                      hover:bg-surface rounded transition-colors`}
                    style={{ paddingLeft: `${depth * 12 + 8}px` }}
                >
                    {isOpen ? (
                        <ChevronDown className="w-3 h-3 text-text-muted flex-shrink-0" />
                    ) : (
                        <ChevronRight className="w-3 h-3 text-text-muted flex-shrink-0" />
                    )}
                    {isOpen ? (
                        <FolderOpen className="w-4 h-4 text-amber-400 flex-shrink-0" />
                    ) : (
                        <Folder className="w-4 h-4 text-amber-400 flex-shrink-0" />
                    )}
                    <span className="text-text-secondary truncate">{node.name}</span>
                </button>
                {isOpen && node.children && (
                    <div>
                        {node.children.map(child => (
                            <TreeNodeItem key={child.path} node={child} depth={depth + 1} />
                        ))}
                    </div>
                )}
            </div>
        );
    }

    return (
        <button
            onClick={() => setActiveFile(node.path)}
            className={`w-full group flex items-center gap-2 px-2 py-1.5 text-sm text-left rounded transition-colors
                  ${isActive ? 'bg-accent-primary/10 text-accent-primary font-medium' : 'hover:bg-surface text-text-secondary'}`}
            style={{ paddingLeft: `${depth * 12 + 20}px` }}
        >
            {getFileIcon(node.path)}
            <span className="truncate flex-1">{node.name}</span>
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    deleteFile(node.path);
                }}
                className="opacity-0 group-hover:opacity-100 p-1 hover:bg-accent-error/20 rounded transition-all"
            >
                <Trash2 className="w-3 h-3 text-accent-error" />
            </button>
        </button>
    );
}

export function Sidebar() {
    const { files, addFile, project, promptHistory } = useAppStore();
    const [activeTab, setActiveTab] = useState<'files' | 'history'>('files');
    const tree = buildFileTree(files);

    const handleNewFile = () => {
        const name = prompt('File Name:');
        if (name) addFile(name, '');
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                const content = event.target?.result as string;
                addFile(file.name, content);
            };
            reader.readAsText(file);
        }
    };

    return (
        <div className="w-64 h-full bg-background-secondary border-r border-border flex flex-col">
            {/* Sidebar header */}
            <div className="p-3 border-b border-border">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-xs font-bold text-text-muted uppercase tracking-wider">Explorer</h2>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={handleNewFile}
                            className="p-1.5 rounded hover:bg-surface transition-colors"
                            title="New file"
                        >
                            <Plus className="w-4 h-4 text-text-muted" />
                        </button>
                        <button
                            onClick={() => {
                                const name = prompt('Folder Name:');
                                if (name) addFile(`${name}/.keep`, '');
                            }}
                            className="p-1.5 rounded hover:bg-surface transition-colors"
                            title="New folder"
                        >
                            <FolderPlus className="w-4 h-4 text-text-muted" />
                        </button>
                        <label className="p-1.5 rounded hover:bg-surface transition-colors cursor-pointer" title="Upload files">
                            <Upload className="w-4 h-4 text-text-muted" />
                            <input
                                type="file"
                                className="hidden"
                                onChange={handleFileUpload}
                            />
                        </label>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex p-0.5 rounded-lg bg-background border border-border">
                    <button
                        onClick={() => setActiveTab('files')}
                        className={`flex-1 flex items-center justify-center gap-2 py-1.5 rounded-md text-xs font-medium transition-all
                                ${activeTab === 'files' ? 'bg-surface text-accent-primary shadow-sm' : 'text-text-muted hover:text-text-primary'}`}
                    >
                        <Files className="w-3.5 h-3.5" />
                        Files
                    </button>
                    <button
                        onClick={() => setActiveTab('history')}
                        className={`flex-1 flex items-center justify-center gap-2 py-1.5 rounded-md text-xs font-medium transition-all
                                ${activeTab === 'history' ? 'bg-surface text-accent-primary shadow-sm' : 'text-text-muted hover:text-text-primary'}`}
                    >
                        <Clock className="w-3.5 h-3.5" />
                        History
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-2">
                {activeTab === 'files' ? (
                    <div className="space-y-0.5">
                        {tree.length > 0 ? (
                            tree.map(node => (
                                <TreeNodeItem key={node.path} node={node} />
                            ))
                        ) : (
                            <div className="text-center py-8 text-text-muted text-sm">
                                <p>No files yet</p>
                                <p className="text-xs mt-1">Enter a prompt to generate code</p>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="space-y-4">
                        {promptHistory.length === 0 ? (
                            <div className="p-4 text-center text-xs text-text-muted">
                                No prompts yet
                                <Sparkles className="w-10 h-10 mx-auto mt-4 text-accent-primary/20" />
                            </div>
                        ) : (
                            [...promptHistory].reverse().map((item, i) => (
                                <div key={item.id} className="p-3 rounded-lg bg-surface/50 border border-border/50 hover:border-accent-primary/30 transition-colors group">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-1.5 text-[10px] text-text-muted uppercase tracking-tighter font-bold">
                                            <Sparkles className="w-2.5 h-2.5 text-accent-primary" />
                                            {item.model}
                                        </div>
                                        <div className="text-[10px] text-text-muted tracking-tight">
                                            {new Date(item.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </div>
                                    </div>
                                    <p className="text-[11px] text-text-secondary line-clamp-3 leading-relaxed group-hover:text-text-primary transition-colors italic">
                                        "{item.prompt}"
                                    </p>
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
